import { FilterOptions } from '@app/components/data-table/hooks/use-server-filter-options';
import { ElectricityActivitiesList } from '@shared/api/types';
import { Icons } from '@shared/components/content/icons';
import Unit from '@shared/components/content/unit';
import { HeaderCell } from '@shared/components/data-table/cells/header-cell';
import { NumberCell } from '@shared/components/data-table/cells/number-cell';
import { TextCell } from '@shared/components/data-table/cells/text-cell';
import { SimpleTooltip } from '@shared/components/simple-tooltip';
import { formatQuantity, formatSmallNumber } from '@shared/lib/utils';
import { ColumnDef } from '@tanstack/react-table';

export function electricityTableColumns({
  filters,
}: {
  filters: FilterOptions<['timePeriods', 'sites']>;
}): ColumnDef<ElectricityActivitiesList>[] {
  return [
    {
      id: 'uuid',
      accessorKey: 'uuid',
      enableHiding: true,
      enableColumnFilter: true,
    },
    {
      id: 'period',
      accessorKey: 'period',
      enableHiding: true,
      enableColumnFilter: true,
      meta: {
        label: 'Time period',
        variant: 'multiSelect',
        options: filters.timePeriods,
        sorting: { ascLabel: 'Oldest', descLabel: 'Newest' },
      },
    },
    {
      id: 'site',
      accessorKey: 'site',
      enableColumnFilter: true,
      header: ({ column }) => <HeaderCell column={column} name="Site" />,
      cell: ({ getValue }) => {
        const site = getValue<ElectricityActivitiesList['site']>();
        return <TextCell text={site.name} />;
      },
      meta: {
        label: 'Site',
        variant: 'multiSelect',
        options: filters.sites,
        sorting: { ascLabel: 'A-Z', descLabel: 'Z-A' },
      },
    },
    {
      id: 'electricitySupply',
      accessorKey: 'electricitySupply',
      header: ({ column }) => (
        <HeaderCell column={column} name="Electricity Supply" />
      ),
      cell: ({ getValue }) => {
        const supply =
          getValue<ElectricityActivitiesList['electricitySupply']>();
        return supply ? (
          <TextCell text={supply} />
        ) : (
          <TextCell
            text="Standard location-based"
            className="text-muted-foreground"
          />
        );
      },
    },
    {
      id: 'emissionFactor',
      accessorKey: 'emissionFactor',
      header: ({ column }) => (
        <HeaderCell
          column={column}
          name="Emission Factor"
          numberColumn
          unit={<Unit variant="kgCO2e/kWh" />}
        />
      ),
      cell: ({ getValue }) => {
        const emissionFactor = getValue<number>();
        return <NumberCell number={emissionFactor} />;
      },
    },
    {
      id: 'quantity',
      accessorKey: 'quantity',
      header: ({ column }) => (
        <HeaderCell
          column={column}
          name="Quantity"
          numberColumn
          unit={<Unit variant="kWh" />}
        />
      ),
      cell: ({ getValue }) => {
        const quantity = getValue<number>();
        return <NumberCell number={quantity} formatter={formatQuantity} />;
      },
    },
    {
      id: 'emissions',
      accessorKey: 'emissions',
      header: ({ column }) => (
        <HeaderCell
          column={column}
          name="Emissions"
          numberColumn
          unit={<Unit variant="tCO2e" />}
        />
      ),
      cell: ({ getValue }) => {
        const emissions = getValue<number>();
        return <TextCell text={formatSmallNumber(emissions)} type="number" />;
      },
    },
    {
      id: 'scope3Emissions',
      accessorKey: 'scope3Emissions',
      header: ({ column }) => (
        <HeaderCell
          column={column}
          name="Scope 3 Emissions"
          numberColumn
          unit={<Unit variant="tCO2e" />}
          badge={
            <SimpleTooltip
              trigger={<Icons.info size={16} />}
              content={
                <span>
                  Scope 3 emissions generated from your electricity supply
                </span>
              }
            />
          }
        />
      ),
      cell: ({ getValue }) => {
        const scope3Emissions = getValue<number>();
        return (
          <TextCell text={formatSmallNumber(scope3Emissions)} type="number" />
        );
      },
    },
  ];
}
